import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import {
  getTagsForLand,
  getAllTags,
  createTag,
  createLandTag,
  removeTagFromLand,
} from '@/services/tagsService'
import { LandTagWithDetails, Tag } from '@popety-io/types'
import { useAppDispatch } from '@/store/hooks'
import { getTagsWithLands } from '@/store/slices/tagsSlice'

interface LandTagsContextState {
  tags: LandTagWithDetails[]
  allTags: Tag[]
  loading: boolean
  error: string | null
  isEditing: boolean
  refetch: () => void
  setIsEditing: (editing: boolean) => void
  addTag: (tagId: string, isPrimary: boolean) => Promise<void>
  removeTag: (landTagId: string) => Promise<void>
  createNewTag: (
    value: string,
    color: string,
    isPrimary: boolean
  ) => Promise<void>
  loadAllTags: () => Promise<void>
}

const LandTagsContext = createContext<LandTagsContextState | undefined>(
  undefined
)

interface LandTagsProviderProps {
  children: ReactNode
  landId?: string
}

export const LandTagsProvider: React.FC<LandTagsProviderProps> = ({
  children,
  landId,
}) => {
  const dispatch = useAppDispatch()
  const [tags, setTags] = useState<LandTagWithDetails[]>([])
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  const fetchTags = useCallback(async () => {
    if (!landId) {
      setTags([])
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const fetchedTags = await getTagsForLand(landId)
      setTags(fetchedTags)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tags')
      setTags([])
    } finally {
      setLoading(false)
    }
  }, [landId])

  const loadAllTags = useCallback(async () => {
    try {
      const availableTags = await getAllTags()
      setAllTags(availableTags)
    } catch (err: any) {
      console.error('Failed to load all tags:', err)
    }
  }, [])

  const addTag = useCallback(
    async (tagId: string, isPrimary: boolean) => {
      if (!landId) return

      try {
        setLoading(true)

        // The API automatically handles converting the old primary tag to secondary
        // when a new primary tag is added, so we just need to call createLandTag
        await createLandTag(landId, tagId, isPrimary)
        await fetchTags()

        // Refresh the Redux store to update map colors if this is a primary tag
        if (isPrimary) {
          dispatch(getTagsWithLands())
        }
      } catch (err: any) {
        setError(err.message || 'Failed to add tag')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [landId, fetchTags, dispatch]
  )

  const removeTag = useCallback(
    async (landTagId: string) => {
      try {
        setLoading(true)

        // Check if we're removing a primary tag
        const tagToRemove = tags.find((t) => t.id === landTagId)
        const wasPrimary = tagToRemove?.primary

        await removeTagFromLand(landTagId)
        await fetchTags()

        // Refresh the Redux store to update map colors if this was a primary tag
        if (wasPrimary) {
          dispatch(getTagsWithLands())
        }
      } catch (err: any) {
        setError(err.message || 'Failed to remove tag')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [fetchTags, tags, dispatch]
  )

  const createNewTag = useCallback(
    async (value: string, color: string, isPrimary: boolean) => {
      if (!landId) return

      try {
        setLoading(true)

        // Create the tag first
        const newTag = await createTag(value, color)

        // Then associate it with the land
        await addTag(newTag.id, isPrimary)

        // Reload all tags to include the new one
        await loadAllTags()

        // Note: addTag already handles the Redux refresh for primary tags
      } catch (err: any) {
        setError(err.message || 'Failed to create and add tag')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [landId, addTag, loadAllTags]
  )

  const refetch = () => {
    fetchTags()
  }

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  const value: LandTagsContextState = {
    tags,
    allTags,
    loading,
    error,
    isEditing,
    refetch,
    setIsEditing,
    addTag,
    removeTag,
    createNewTag,
    loadAllTags,
  }

  return (
    <LandTagsContext.Provider value={value}>
      {children}
    </LandTagsContext.Provider>
  )
}

export const useLandTags = (): LandTagsContextState => {
  const context = useContext(LandTagsContext)
  if (context === undefined) {
    throw new Error('useLandTags must be used within a LandTagsProvider')
  }
  return context
}
